import React, { useEffect, useRef, useState } from 'react'
import { t } from '../i18n'
import { api, hasProfile } from '../Services/api'
import { track } from '../Utils/analytics'
import {
  ACROSS,
  DOWN,
  formatCount,
  formatTime,
  longDate,
  monthTitle,
  parseDay,
  shortDate,
  toDay,
  weekdayInitials,
} from '../Utils/crossword'
import { cardTags, crosswordCard } from '../Utils/shareCard'
import { CheckIcon, ChevronLeft, ChevronRight, CloseIcon, Spinner } from './Icons'

/* Križaljka's overlays: a bottom sheet on phones, a dialog on desktop.
   Escape and the backdrop close it; focus goes in and comes back. */
export function Sheet({ label, onClose, children, className = '', dismissable = true }) {
  const panel = useRef(null)
  useEffect(() => {
    const previous = document.activeElement
    panel.current?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape' && dismissable) {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    document.body.classList.add('no-scroll')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('no-scroll')
      previous?.focus?.()
    }
  }, [onClose, dismissable])
  return (
    <div className="cw-sheet-wrap">
      <button
        type="button"
        className="cw-sheet-backdrop"
        aria-label={t('common.close')}
        tabIndex={-1}
        onClick={dismissable ? onClose : undefined}
      />
      <section
        ref={panel}
        className={`cw-sheet ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        <span className="cw-sheet-handle" aria-hidden="true" />
        {dismissable && (
          <button
            type="button"
            className="icon-button cw-sheet-close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <CloseIcon size={20} />
          </button>
        )}
        {children}
      </section>
    </div>
  )
}

/* Vodoravno / Uspravno, the current clue highlighted, solved ones crossed out. */
export function ClueList({ game, puzzle, onPick, className = '' }) {
  return (
    <section className={`cw-clue-list ${className}`} aria-label={t('crossword.allClues')}>
      {[
        [ACROSS, t('crossword.across')],
        [DOWN, t('crossword.down')],
      ].map(([dir, title]) => (
        <div key={dir}>
          <h2 className="section-title cw-clue-group">{title}</h2>
          {puzzle.words
            .filter((w) => w.dir === dir)
            .map((w) => (
              <button
                key={w.key}
                type="button"
                className={`cw-clue-item ${w === game.current ? 'current' : ''} ${
                  game.done.has(w.key) ? 'done' : ''
                }`}
                onClick={() => onPick(w)}
              >
                <span className="cw-clue-num">{w.number}</span>
                <span className="cw-clue-text">{w.text}</span>
              </button>
            ))}
        </div>
      ))}
    </section>
  )
}

export function MenuSheet({ game, onClose }) {
  const act = (fn) => () => {
    fn()
    onClose()
  }
  return (
    <Sheet label={t('crossword.more')} onClose={onClose} className="cw-menu">
      <div className="cw-menu-list">
        <button type="button" onClick={act(game.revealLetter)} disabled={game.progress.solved}>
          {t('crossword.revealLetter')}
        </button>
        <button type="button" onClick={act(game.revealWord)} disabled={game.progress.solved}>
          {t('crossword.revealWord')}
        </button>
        <button type="button" className="danger" onClick={act(game.restart)}>
          {t('crossword.restart')}
        </button>
      </div>
      <p className="cw-note">{t('crossword.revealNote')}</p>
    </Sheet>
  )
}

function Avatar({ initials, color, me = false }) {
  return (
    <span className={`cw-avatar ${me ? 'me' : ''}`} style={me ? undefined : { background: color }}>
      {initials}
    </span>
  )
}

const playersLine = (stats) =>
  [
    t('crossword.players', { count: stats.solvedCount, n: formatCount(stats.solvedCount) }),
    stats.medianSeconds != null && t('crossword.median', { time: formatTime(stats.medianSeconds) }),
  ]
    .filter(Boolean)
    .join(' · ')

/* Top 10 of the day, and "Ti" with your place. */
export function TopSheet({ puzzle, isToday, game, onClose, notify }) {
  const [top, setTop] = useState(null)
  const [me, setMe] = useState(null)
  const [mine, setMine] = useState(game.result && !game.result.error ? game.result : null)
  useEffect(() => {
    let alive = true
    api
      .crosswordTop(puzzle.date)
      .then((d) => alive && setTop(d))
      .catch(() => alive && setTop({ error: true }))
    if (hasProfile()) {
      api
        .crosswordMe()
        .then((d) => alive && setMe(d))
        .catch(() => {})
      api
        .crosswordResult(puzzle.date)
        .then((d) => alive && d && setMine(d))
        .catch(() => {})
    }
    return () => {
      alive = false
    }
  }, [puzzle.date])

  const rename = () =>
    api
      .crosswordNewName()
      .then((d) => d && setMe(d))
      .catch(() => notify(t('crossword.failed')))

  const title = isToday
    ? t('crossword.topToday')
    : t('crossword.topDay', { date: shortDate(puzzle.date) })
  return (
    <Sheet label={title} onClose={onClose} className="cw-top">
      <div className="cw-sheet-head">
        <h2>{title}</h2>
        {top && !top.error && <span className="muted">{playersLine(top)}</span>}
      </div>
      {top?.error && <p className="muted">{t('crossword.loadError')}</p>}
      {top && !top.error && top.top.length === 0 && (
        <p className="muted">{t('crossword.topEmpty')}</p>
      )}
      {top && !top.error && top.top.length > 0 && (
        <ol className="cw-top-list">
          {top.top.map((p) => (
            <li key={p.rank} className="cw-top-row">
              <span className={`cw-rank rank-${p.rank}`}>{p.rank}</span>
              <Avatar initials={p.initials} color={p.color} />
              <span className="cw-top-name">{p.name}</span>
              <span className="cw-top-time">{formatTime(p.seconds)}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="cw-top-me">
        <span className="cw-rank-me">{mine?.rank ? `#${mine.rank}` : '–'}</span>
        <Avatar initials={t('crossword.you')} me />
        <span className="cw-top-name">{t('crossword.you')}</span>
        <span className="cw-top-time">
          {mine?.solved ? formatTime(mine.seconds) : t('crossword.notSolved')}
        </span>
      </div>
      {me && (
        <p className="cw-note">
          {t('crossword.nameLine', { name: me.name })}{' '}
          <button type="button" className="link-orange cw-link-button" onClick={rename}>
            {t('crossword.newName')}
          </button>
        </p>
      )}
      <p className="cw-note">{t('crossword.topNote')}</p>
    </Sheet>
  )
}

function StatTiles({ stats, record }) {
  return (
    <div className="cw-stats">
      <div className="cw-stat">
        <strong>{stats ? stats.currentStreak : '–'}</strong>
        <span>{t('crossword.streak', { count: stats ? stats.currentStreak : 0 })}</span>
      </div>
      <div className={`cw-stat ${record ? 'record' : ''}`}>
        <strong>{stats?.bestSeconds != null ? formatTime(stats.bestSeconds) : '–'}</strong>
        <span>{t('crossword.best')}</span>
      </div>
      <div className="cw-stat">
        <strong>{stats ? stats.solvedCount : '–'}</strong>
        <span>{t('crossword.solvedTotal')}</span>
      </div>
    </div>
  )
}

/* "Riješeno za 1:12": how you did against today's players, streak, best
   time, sharing. */
export function SolvedSheet({ puzzle, isToday, game, onClose, onShare }) {
  const r = game.result
  const [stats, setStats] = useState(r?.stats || null)
  useEffect(() => {
    if (r?.stats) {
      setStats(r.stats)
      return undefined
    }
    if (!hasProfile()) return undefined
    let alive = true
    api
      .crosswordMe()
      .then((d) => alive && d && setStats(d.stats))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [r])

  const seconds = r?.seconds ?? game.progress.secs
  const helped = r && !r.error ? r.helped : game.helped
  const time = formatTime(seconds)
  let sub = t('crossword.subClean')
  if (helped) sub = t('crossword.subHelped')
  else if (r?.isRecord) sub = t('crossword.subRecord', { time: formatTime(r.previousBestSeconds) })

  let compare = null
  if (r && !r.error) {
    if (r.ranked && r.rank === 1) compare = { title: t('crossword.fastest'), pct: 100 }
    else if (r.ranked && r.fasterThanPercent != null)
      compare = {
        title: t('crossword.fasterThan', { percent: r.fasterThanPercent }),
        pct: r.fasterThanPercent,
      }
    else if (helped) compare = { title: t('crossword.helpedNoCompare'), pct: 0 }
    else if (!r.onDay) compare = { title: t('crossword.archiveNoCompare'), pct: 0 }
    else if (!r.ranked) compare = { title: t('crossword.notCompared'), pct: 0 }
  }
  return (
    <Sheet label={t('crossword.solvedLabel')} onClose={onClose} className="cw-solved">
      <span className="cw-solved-icon">
        <CheckIcon size={30} strokeWidth={2.4} />
      </span>
      {r?.isRecord && <span className="cw-record">{t('crossword.newRecord')}</span>}
      <h2>{t('crossword.solvedIn', { time })}</h2>
      <p className="cw-solved-sub">{sub}</p>
      {r?.error && <p className="cw-note">{t('crossword.offline')}</p>}
      {compare && (
        <div className="cw-compare">
          <strong>{compare.title}</strong>
          <span className="cw-bar">
            <span style={{ width: `${Math.max(0, Math.min(100, compare.pct))}%` }} />
          </span>
          <span className="muted">{playersLine(r)}</span>
        </div>
      )}
      {!r?.error && <StatTiles stats={stats} record={Boolean(r?.isRecord)} />}
      <button
        type="button"
        className="btn btn-primary cw-share"
        onClick={() => onShare({ seconds, helped, stats })}
      >
        {t('crossword.share')}
      </button>
      <button type="button" className="btn-plain" onClick={onClose}>
        {t('crossword.viewGrid')}
      </button>
      {isToday && <span className="cw-note">{t('crossword.tomorrow')}</span>}
    </Sheet>
  )
}

const SHARE_URL = 'https://instapres.com/krizaljka'

/* "Podijeli rezultat": the result as a picture (Utils/shareCard.js), made as
   the sheet opens so sharing and copying run straight from the tap (Safari
   allows neither after a wait). Phones share it through the system sheet,
   desktops copy it; downloading it and copying the text always work. */
export function ShareSheet({ puzzle, game, share, onClose, notify }) {
  const [card, setCard] = useState(null) // { blob, url, file } | { error: true }
  const text =
    t('crossword.shareText', { date: shortDate(puzzle.date), time: formatTime(share.seconds) }) +
    (share.helped ? t('crossword.shareHelped') : '')
  const fileName = `instapres-krizaljka-${puzzle.date}.png`
  const result = game.result && !game.result.error ? game.result : null
  const revealed = game.progress.revealed

  useEffect(() => {
    let alive = true
    let url = null
    crosswordCard({
      puzzle,
      seconds: share.seconds,
      revealed,
      tags: cardTags({ result, stats: share.stats, helped: share.helped }),
    })
      .then((blob) => {
        if (!alive) return
        url = URL.createObjectURL(blob)
        setCard({ blob, url, file: new File([blob], fileName, { type: 'image/png' }) })
      })
      .catch(() => alive && setCard({ error: true }))
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [puzzle, share, result, revealed, fileName])

  const ready = card && !card.error
  const canShare = Boolean(ready && navigator.canShare?.({ files: [card.file] }))
  const canCopy = Boolean(ready && window.ClipboardItem && navigator.clipboard?.write)
  const done = (how) => track('crossword_share', { date: puzzle.date, how })

  const actions = {
    share: async () => {
      try {
        await navigator.share({ files: [card.file], text: `${text}\n${SHARE_URL}` })
        done('share')
      } catch (e) {
        if (e?.name !== 'AbortError') notify(t('crossword.shareFailed'))
      }
    },
    copy: async () => {
      try {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': card.blob })])
        notify(t('crossword.imageCopied'))
        done('copy_image')
      } catch (e) {
        notify(t('crossword.imageCopyFailed'))
      }
    },
    download: () => {
      const a = document.createElement('a')
      a.href = card.url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      done('download')
    },
    text: async () => {
      try {
        await navigator.clipboard.writeText(`${text}\n${SHARE_URL}`)
        notify(t('crossword.copied', { text }))
        done('copy_text')
      } catch (e) {
        notify(t('crossword.copyFailed'))
      }
    },
  }
  // The best way this browser has goes first, as the main button.
  const order = [canShare && 'share', canCopy && 'copy', ready && 'download'].filter(Boolean)
  const labels = {
    share: t('crossword.shareNative'),
    copy: t('crossword.copyImage'),
    download: t('crossword.downloadImage'),
  }

  return (
    <Sheet label={t('crossword.share')} onClose={onClose} className="cw-share-sheet">
      <div className="cw-sheet-head">
        <h2>{t('crossword.share')}</h2>
      </div>
      <div className="cw-card-preview">
        {ready && <img src={card.url} alt={t('crossword.cardAlt')} width="1080" height="1350" />}
        {!card && (
          <span className="cw-card-wait">
            <Spinner size={22} />
            {t('crossword.making')}
          </span>
        )}
        {card?.error && <span className="cw-card-wait">{t('crossword.cardFailed')}</span>}
      </div>
      <div className="cw-share-actions">
        {order.map((key, i) => (
          <button
            key={key}
            type="button"
            className={`btn ${i === 0 ? 'btn-primary' : 'btn-secondary'}`}
            onClick={actions[key]}
          >
            {labels[key]}
          </button>
        ))}
        <button type="button" className="btn-plain" onClick={actions.text}>
          {t('crossword.copyText')}
        </button>
      </div>
    </Sheet>
  )
}

const monthKey = (first) => toDay(first).slice(0, 7)

/* Arhiva: a month calendar, solved days in orange; any earlier day can be
   played (it doesn't count toward the streak). onPick(day), null = today's. */
export function ArchiveSheet({ current, onPick, onClose }) {
  const [first, setFirst] = useState(() => {
    const d = parseDay(current)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [days, setDays] = useState(null)
  const [mine, setMine] = useState({})
  const [me, setMe] = useState(null)
  const month = monthKey(first)

  useEffect(() => {
    if (!hasProfile()) return undefined
    let alive = true
    api
      .crosswordMe()
      .then((d) => alive && setMe(d))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    let alive = true
    setDays(null)
    api
      .crosswordMonth(month)
      .then((d) => alive && setDays(d))
      .catch(() => alive && setDays({ error: true }))
    if (hasProfile())
      api
        .crosswordResults(month)
        .then((d) => {
          if (!alive || !d) return
          const out = {}
          d.results.forEach((r) => {
            out[r.date] = r
          })
          setMine(out)
        })
        .catch(() => {})
    return () => {
      alive = false
    }
  }, [month])

  const today = days?.today || current
  const available = new Set((days?.days || []).map((d) => d.date))
  const firstDate = days?.firstDate ? parseDay(days.firstDate) : null
  const canPrev = firstDate
    ? first > new Date(firstDate.getFullYear(), firstDate.getMonth(), 1)
    : false
  const todayDate = parseDay(today)
  const canNext = first < new Date(todayDate.getFullYear(), todayDate.getMonth(), 1)
  const lead = (first.getDay() + 6) % 7 // weeks start on Monday
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const cells = [
    ...Array.from({ length: lead }, (_, i) => ({ blank: true, key: `b${i}` })),
    ...Array.from({ length: count }, (_, i) => {
      const day = toDay(new Date(first.getFullYear(), first.getMonth(), i + 1))
      return { day, n: i + 1, key: day }
    }),
  ]
  const stats = me?.stats
  return (
    <Sheet label={t('crossword.archiveTitle')} onClose={onClose} className="cw-archive">
      <div className="cw-sheet-head">
        <h2>{t('crossword.archiveTitle')}</h2>
        {days?.today && (
          <span className="muted">{t('crossword.todayIs', { date: longDate(days.today) })}</span>
        )}
      </div>
      <StatTiles stats={stats} />
      <div className="cw-month-head">
        <strong>{monthTitle(first)}</strong>
        <span>
          <button
            type="button"
            className="icon-button"
            aria-label={t('crossword.prevMonth')}
            disabled={!canPrev}
            onClick={() => setFirst(new Date(first.getFullYear(), first.getMonth() - 1, 1))}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('crossword.nextMonth')}
            disabled={!canNext}
            onClick={() => setFirst(new Date(first.getFullYear(), first.getMonth() + 1, 1))}
          >
            <ChevronRight size={18} />
          </button>
        </span>
      </div>
      <div className="cw-calendar" aria-busy={days === null}>
        {weekdayInitials().map((w, i) => (
          <span key={`w${i}`} className="cw-weekday" aria-hidden="true">
            {w}
          </span>
        ))}
        {cells.map((cell) => {
          if (cell.blank) return <span key={cell.key} />
          const playable = available.has(cell.day)
          const r = mine[cell.day]
          const solved = Boolean(r?.solved)
          const label = `${longDate(cell.day)}${
            playable ? `, ${solved ? t('crossword.daySolved') : t('crossword.dayNotSolved')}` : ''
          }`
          return (
            <button
              key={cell.key}
              type="button"
              className={`cw-day ${solved ? 'solved' : ''} ${cell.day === today ? 'today' : ''} ${
                cell.day === current ? 'current' : ''
              }`}
              disabled={!playable}
              aria-label={label}
              onClick={() => onPick(cell.day === today ? null : cell.day)}
            >
              {cell.n}
              {solved && (
                <span className="cw-day-check" aria-hidden="true">
                  <CheckIcon size={11} strokeWidth={3.4} />
                </span>
              )}
            </button>
          )
        })}
      </div>
      <div className="cw-legend">
        <span>
          <i className="solved" />
          {t('crossword.legendSolved')}
        </span>
        <span>
          <i />
          {t('crossword.legendOpen')}
        </span>
      </div>
      <p className="cw-note">{t('crossword.archiveNote')}</p>
    </Sheet>
  )
}

export function CluesSheet({ game, puzzle, onClose }) {
  return (
    <Sheet label={t('crossword.allClues')} onClose={onClose} className="cw-clues-sheet">
      <ClueList
        game={game}
        puzzle={puzzle}
        onPick={(w) => {
          game.pickWord(w)
          onClose()
        }}
      />
    </Sheet>
  )
}
